import type { ReactNode } from 'react';
import { Copy, LifeBuoy, LogOut, Moon, Scale, Settings, Settings2, Sun } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { MenuSeparator } from './MenuSeparator';
import { SidebarLanguageMenu } from './SidebarLanguageMenu';
import { RAIL_POPOVER_SIDE_OFFSET } from './railPopover.constants';
import { useTranslation } from 'react-i18next';

interface SidebarUserMenuItem {
  id: string;
  label: ReactNode;
  onClick: () => void;
  /** Menu-row icon (~16px, monochrome). Falls back to a generic settings icon. */
  icon?: ReactNode;
}

export interface SidebarUserPopoverProps {
  /** Whether the popover is open. */
  open: boolean;
  /** Called when the popover open state should change. */
  onOpenChange: (open: boolean) => void;
  /** User display name. */
  userName: string;
  /** User email address. */
  userEmail: string;
  /** Additional custom menu items (e.g. "Studio settings"). */
  customMenuItems?: SidebarUserMenuItem[];
  /** Whether to show the "Admin settings" link. */
  showAdminSettings?: boolean;
  /** Platform app base URL (used for admin settings / legal links). */
  platformAppUrl: string;
  /** Whether the "email copied" state is active. */
  emailCopied?: boolean;
  /** Called when the user clicks "Get support" (copy email). */
  onCopySupport: () => void;
  /** Whether to offer the language switcher; caller resolves `language_switcher`. */
  showLanguageSwitcher?: boolean;
  /** Whether dark mode is active. */
  isDarkMode?: boolean;
  /** Called when the user toggles dark/light mode. */
  onToggleTheme?: () => void;
  /** Called when the user clicks "Sign out". */
  onSignOut: () => void;
  /** The trigger element (typically SidebarUserTrigger). */
  children: ReactNode;
}

function MenuItemButton({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent"
    >
      <span className="flex size-md shrink-0 items-center justify-center text-muted-foreground">
        {icon}
      </span>
      {label}
    </button>
  );
}

/**
 * User menu popover — appears above the user avatar at the bottom of the sidebar.
 *
 * Layout:
 * 1. User name + email (non-clickable header)
 * 2. Divider
 * 3. Custom menu items (e.g., "Studio settings")
 * 4. Admin settings → opens platform URL in new tab
 * 5. Legal information → opens platform URL in new tab
 * 6. Get support → copies support@monksflow.ai to clipboard
 * 7. Divider
 * 8. Sign out
 */
export function SidebarUserPopover({
  open,
  onOpenChange,
  userName,
  userEmail,
  customMenuItems = [],
  showAdminSettings = false,
  platformAppUrl,
  emailCopied = false,
  onCopySupport,
  showLanguageSwitcher = false,
  isDarkMode = false,
  onToggleTheme,
  onSignOut,
  children,
}: SidebarUserPopoverProps) {
  const { t } = useTranslation('common');

  const openInNewTab = (path: string) => {
    window.open(`${platformAppUrl}${path}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="right"
        sideOffset={RAIL_POPOVER_SIDE_OFFSET}
        align="end"
        className="w-[220px] p-0"
      >
        {/* User info header */}
        <div className="px-3 py-2.5">
          <p className="truncate text-sm font-medium text-foreground">{userName}</p>
          <p className="truncate text-xs text-muted-foreground">{userEmail}</p>
        </div>

        <MenuSeparator />

        {/* Custom menu items. The separator is theirs rather than the group below,
            so it disappears with them instead of leaving a divider against the header. */}
        {customMenuItems.length > 0 && (
          <>
            <div className="py-1">
              {customMenuItems.map((item) => (
                <MenuItemButton
                  key={item.id}
                  icon={item.icon ?? <Settings2 className="size-md" />}
                  label={item.label}
                  onClick={item.onClick}
                />
              ))}
            </div>
            <MenuSeparator />
          </>
        )}

        {/* Standard items */}
        <div className="py-1">
          {showAdminSettings && (
            <MenuItemButton
              icon={<Settings className="size-md" />}
              label={t('appShell.sidebarUserPopover.adminSettings')}
              onClick={() => openInNewTab('/admin')}
            />
          )}
          {showLanguageSwitcher && <SidebarLanguageMenu onSelected={() => onOpenChange(false)} />}
          <MenuItemButton
            icon={<Scale className="size-md" />}
            label={t('appShell.sidebarUserPopover.legalInformation')}
            onClick={() => openInNewTab('/legal-information')}
          />
          <MenuItemButton
            icon={emailCopied ? <Copy className="size-md" /> : <LifeBuoy className="size-md" />}
            label={
              emailCopied
                ? t('appShell.sidebarUserPopover.emailCopied')
                : t('appShell.sidebarUserPopover.getSupport')
            }
            onClick={onCopySupport}
          />
          {/* Dark mode toggle — gated by the `dark_mode_toggle` Statsig flag.
              When the flag is off, `onToggleTheme` is undefined and this item is
              not rendered. */}
          {onToggleTheme && (
            <MenuItemButton
              icon={isDarkMode ? <Sun className="size-md" /> : <Moon className="size-md" />}
              label={
                isDarkMode
                  ? t('appShell.sidebarUserPopover.lightMode')
                  : t('appShell.sidebarUserPopover.darkMode')
              }
              onClick={onToggleTheme}
            />
          )}
        </div>

        <MenuSeparator />

        {/* Sign out */}
        <div className="py-1">
          <MenuItemButton
            icon={<LogOut className="size-md" />}
            label={t('appShell.sidebarUserPopover.signOut')}
            onClick={onSignOut}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
